/**
 * TrialPurchaseRestrictedError — typed error thrown by the credits services
 * when leadbilling rejects a one-time credit purchase because the workspace
 * subscription is still in its trial (BUG-527 gate).
 *
 * The wire shape is a 422 with a flat top-level discriminator:
 *   `{ error: "trial_workspace_restriction", message: "..." }`
 * Ordinary validation 422s on the same endpoint carry the plural `errors`
 * field-bag instead, so the two never collide.
 *
 * BUG-775 — before this existed, the trial rejection collapsed into the
 * generic 422 `validation` classification and surfaced as "Algo salió mal.
 * Intenta de nuevo en unos minutos", which reads as a transient payment
 * failure and invites retries the backend can never satisfy. The gate fires
 * before any Stripe side effect, so no card is ever charged.
 */
export class TrialPurchaseRestrictedError extends Error {
  /** Discriminator matching the repo's `tClassifiedError` pattern. */
  readonly type = 'trial_purchase_restricted' as const;

  constructor(
    message = 'Trial workspaces cannot purchase one-time credit products.',
  ) {
    super(message);
    this.name = 'TrialPurchaseRestrictedError';
    // Restore prototype chain after extending built-in Error (TS target ES5+ safety).
    Object.setPrototypeOf(this, TrialPurchaseRestrictedError.prototype);
  }
}

/** Machine code leadbilling emits in the 422 body's top-level `error` field. */
export const TRIAL_PURCHASE_RESTRICTED_CODE = 'trial_workspace_restriction';

/** Type-guard for non-instanceof contexts (e.g. structured-clone boundaries). */
export const isTrialPurchaseRestrictedError = (
  err: unknown,
): err is TrialPurchaseRestrictedError =>
  err instanceof TrialPurchaseRestrictedError ||
  (err instanceof Error &&
    'type' in err &&
    (err as { type?: unknown }).type === 'trial_purchase_restricted');
